/**
 * @file ContractFromPricesPanel.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-11-03
 * @description Making prices into a contract — the one panel, and its rules,
 *              for both ways in (Cam, 28 Oct 2026 — N4: "Add the option to
 *              right click/ three dots menus (including when multiple
 *              selected) in pricing and costing to 'Contract these prices' and
 *              handle as we will do the contract generation from rep xlsx
 *              import"):
 *                - the rep-reply import's "Also make this a contract price"
 *                  (RepRepliesImportModal, Part C — 20 / 21 / 28 Oct 2026);
 *                - Pricing & Costing's "Contract these prices" window
 *                  (ContractPricesModal).
 *              Moved here unchanged from RepRepliesImportModal 1.5.0 so the two
 *              can never part: a New contract (a period of that category, or
 *              From / To dates covering every delivery; the order's delivery
 *              terms; this customer or its pricing group; quantity blank unless
 *              typed — O2) of this row's product, or of several rows' products
 *              of one customer and category ("Add another row's product to
 *              this contract…" — ONE contract, its settings shared, keyed A, B,
 *              …); or Add to an existing contract of the customer's, choosing
 *              what happens to each product it already holds (keep, put on
 *              hold, archive, replace). The server's rules are the same
 *              (services/repReplyContractService).
 *
 *   1.1.0 (2026-10-29 — Cam: product groups "can then inform contract
 *   pricing"; "Plain product only, same pack"): a row whose product is in a
 *   live product group offers, on a New contract, "Add the rest of <group>'s
 *   plain products to this contract" (contract_offer.product_group — its
 *   other plain products of the row's category and pack, and those left out
 *   with why). Each goes on as a product of the contract at the row's price
 *   unless typed otherwise; sent as `group_products` + `product_group_id`
 *   (services/repReplyContractService checks them again). Greyed "Not ready"
 *   before migrations/contract_product_groups.sql.
 *
 *   1.2.0 (2026-11-03 — Reports › Contracts Review): an offer made from
 *   INVOICED sales carries `no_cover` (a new contract is for the period chosen
 *   — the dates sold need not be covered, so no "does not cover every
 *   delivery") and `no_link` (nothing is linked — the summary says the
 *   invoices are unchanged instead of "N lines linked").
 *
 *   A "row" is { row (its label number), price, contract_offer (the server's
 *   offer — repReplyContractService.offersFor), contract_requested? / a rep's
 *   request }. `offers[i]` is the panel's state for row i: { on, mode,
 *   periodId, useDates, start, end, scope, quantity, contractKey, others,
 *   shared }; `shared[label]` the settings of a shared new contract.
 */

import React from 'react';
import Dropdown from './Dropdown';
import Input from './Input';
import DateInput from './DateInput';
import { formatQuantity, formatCount } from '../utils/numberFormatters';
import { money, contractGroupText, CONTRACT_OTHER_ACTIONS, ukDate } from '../utils/pricingCosting';
import { memberText } from '../utils/contractProductGroups';
import './PricingCosting.css';
import './ContractProductGroupPicker.css';

/** Is a row's contract offer switched on and usable? */
export const offerOn = (r, o) => !!(o && o.on && r && r.contract_offer && r.contract_offer.available);

/** The settings a row's NEW contract is made with: its own, or its shared contract's. */
export function settingsOf(o, shared = {}) {
  if (!o) return {};
  return o.shared && shared[o.shared] ? { ...o, ...shared[o.shared], mode: 'new' } : o;
}

/** Shared new contracts: label → the row indexes in it (switched on, new). */
export function sharedMembers(rows, offers = {}) {
  const out = new Map();
  rows.forEach((r, i) => {
    const o = offers[i];
    if (!offerOn(r, o) || o.mode === 'existing' || !o.shared) return;
    if (!out.has(o.shared)) out.set(o.shared, []);
    out.get(o.shared).push(i);
  });
  return out;
}

/** The next free label for a shared new contract: A, B, … */
export function nextLabel(shared = {}) {
  for (let n = 0; n < 26 * 27; n++) {
    const l = n < 26 ? String.fromCharCode(65 + n) : `${String.fromCharCode(64 + Math.floor(n / 26))}${String.fromCharCode(65 + (n % 26))}`;
    if (!shared[l]) return l;
  }
  return `X${Object.keys(shared).length}`;
}

/** The dates every row of a contract delivers on: { from, to }. */
function datesOf(rows, idx) {
  // An offer from invoiced sales (no_cover) need not cover its dates (1.2.0).
  const ds = idx.filter(i => !rows[i].contract_offer?.no_cover).map(i => rows[i].contract_offer?.dates).filter(Boolean);
  if (!ds.length) return null;
  return { from: ds.map(d => d.from).sort()[0], to: ds.map(d => d.to).sort().slice(-1)[0] };
}

/** Why a switched-on contract offer cannot go yet, or null. */
export function offerIncomplete(r, o) {
  if (!offerOn(r, o)) return null;
  if (o.mode === 'existing') return o.contractKey ? null : 'choose the contract to add to';
  if (o.useDates) return o.start && o.end ? (o.start > o.end ? 'To is before From' : null) : 'choose From and To';
  return o.periodId ? null : 'choose a period';
}

/**
 * A new contract's quantity as typed: null when left blank (the contract's
 * quantity stays blank), the figure, or NaN when it is not one.
 */
export function quantityTyped(raw) {
  const t = String(raw == null ? '' : raw).replace(/,/g, '').trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? n : NaN;
}

/**
 * The product group products a new contract takes with it (1.1.0): from each
 * of its rows whose "Add the rest of <group>" is on, the group's plain
 * products not already a product of the contract, each at the price typed
 * for it, else its row's. Pure.
 * @returns {{ groupId: number|null, groupName: string|null, groups: number,
 *   products: Array<{ item_id, item_code, item_name, price }> }}
 */
export function groupProductsFor(rows, offers = {}, members = []) {
  const inContract = new Set(members.map(k => rows[k] && rows[k].contract_offer && rows[k].contract_offer.item_id));
  const products = [];
  const groupIds = new Set();
  let groupId = null;
  let groupName = null;
  for (const k of members) {
    const o = offers[k];
    const g = rows[k] && rows[k].contract_offer && rows[k].contract_offer.product_group;
    if (!o || !o.groupAdd || !o.groupAdd.on || !g || !g.ready) continue;
    for (const m of g.add || []) {
      if (inContract.has(m.item_id) || products.some(x => x.item_id === m.item_id)) continue;
      const raw = (o.groupAdd.prices || {})[m.item_id];
      const typed = raw !== undefined && raw !== null && String(raw).trim() !== '';
      products.push({ item_id: m.item_id, item_code: m.item_code, item_name: m.item_name, price: typed ? Number(raw) : Number(rows[k].price) });
      groupIds.add(g.id);
      if (groupId === null) { groupId = g.id; groupName = g.name; }
    }
  }
  return { groupId, groupName, groups: groupIds.size, products };
}

/**
 * Why row i's contract cannot be written as set, or null — unfinished, or (a
 * shared new contract) its products of other customers or categories, the same
 * product twice, or dates not covering every delivery.
 */
export function contractProblem(i, rows, offers = {}, shared = {}) {
  const r = rows[i];
  const o = offers[i];
  if (!r || !offerOn(r, o)) return null;
  if (o.mode === 'existing') return offerIncomplete(r, o);
  const members = o.shared ? (sharedMembers(rows, offers).get(o.shared) || [i]) : [i];
  const eff = settingsOf(o, shared);
  if (members.length > 1) {
    const offs = members.map(k => rows[k].contract_offer);
    if (new Set(offs.map(x => x.account_id)).size > 1) return 'its products are for different customers — a contract is for one customer';
    if (new Set(offs.map(x => x.category_id)).size > 1) return `its products are in different categories (${[...new Set(offs.map(x => x.category))].join(', ')}) — a contract holds one category`;
    const seen = new Map();
    for (const k of members) {
      const code = rows[k].contract_offer.item_id;
      if (seen.has(code)) return `${rows[k].contract_offer.item_code} is in it twice (rows ${rows[seen.get(code)].row} and ${rows[k].row}) — one price per product`;
      seen.set(code, k);
    }
  }
  const unfinished = offerIncomplete(r, { ...eff, on: true, mode: 'new' });
  if (unfinished) return unfinished;
  const gp = groupProductsFor(rows, offers, members);
  if (gp.groups > 1) return 'it takes the other products of two product groups — a contract is made from one group';
  const unpriced = gp.products.filter(x => !(Number(x.price) > 0));
  if (unpriced.length) return `a price above £0 is needed for ${unpriced.map(x => x.item_code).join(', ')} (from ${gp.groupName})`;
  if (Number.isNaN(quantityTyped(eff.quantity))) return 'the quantity is not a figure — leave it blank, or type a figure of 0 or more';
  const d = datesOf(rows, members);
  if (d) {
    let start = eff.start;
    let end = eff.end;
    if (!eff.useDates) {
      const p = (r.contract_offer.periods || []).find(x => String(x.id) === String(eff.periodId));
      start = p ? p.start_date : null;
      end = p ? p.end_date : null;
    }
    if (start && end && (start > d.from || end < d.to)) {
      return `${ukDate(start)} – ${ukDate(end)} does not cover every delivery (${ukDate(d.from)}${d.to !== d.from ? ` – ${ukDate(d.to)}` : ''})`;
    }
  }
  return null;
}

/** The default answers to a row's existing contract's other products. */
export function defaultOthers(group) {
  const o = {};
  (group?.products || []).forEach(p => { o[p.id] = p.same_product ? 'replace' : 'keep'; });
  return o;
}

/** A new contract's dates, scope and quantity (blank unless typed), as the server takes them. */
export function newContractFields(eff) {
  const c = {};
  if (eff.useDates) { c.start_date = eff.start; c.end_date = eff.end; } else c.period_id = eff.periodId;
  c.apply_scope = eff.scope === 'group' ? 'group' : 'parent';
  const q = quantityTyped(eff.quantity);
  if (q !== null) c.quantity = q;
  return c;
}

/** "18.00 T", "40 × 25kg bag" — the orders' total beside the quantity field. */
export const linesQtyText = (q, unit) => {
  if (q === null || q === undefined) return '';
  if (!unit) return formatQuantity(q);
  if (unit === 'T') return `${formatQuantity(q)} T`;
  return `${formatCount(q)} × ${unit}`;
};

/** "quantity blank (set it on the contract later)" / "quantity 12.00 T". */
function quantityWords(eff, co) {
  const q = quantityTyped(eff.quantity);
  if (q === null || q === 0 || Number.isNaN(q)) return 'quantity blank (set it on the contract when you know it)';
  return `quantity ${linesQtyText(q, co && co.unit)}`;
}

/** "Winter 2026-27 (01/10/26 – 30/04/27)" or the dates chosen. */
function whenText(co, eff) {
  const p = eff.useDates ? null : (co.periods || []).find(x => String(x.id) === String(eff.periodId));
  return p ? `${p.name} (${ukDate(p.start_date)} – ${ukDate(p.end_date)})` : `${ukDate(eff.start)} – ${ukDate(eff.end)}`;
}

/**
 * The contracts the server is sent (POST …/rep-replies `contracts`, POST
 * …/contract-prices): one per row switched on, a shared new contract once with
 * `products`. `idsOf(i)` — the order lines of row i that will be written (a
 * row with none sends nothing); `extra(i)` — anything more a product carries
 * (Pricing & Costing: where its price came from).
 */
export function contractsPayload({ rows, offers = {}, shared = {}, idsOf, extra = () => ({}) }) {
  const contracts = [];
  const groups = sharedMembers(rows, offers);
  const sent = new Set();
  rows.forEach((r, i) => {
    const ids = idsOf(i);
    if (!ids.length) return;
    const o = offers[i];
    if (!offerOn(r, o)) return;
    if (o.mode !== 'existing' && o.shared && (groups.get(o.shared) || []).length > 1) {
      if (sent.has(o.shared)) return;
      sent.add(o.shared);
      const products = groups.get(o.shared)
        .map(k => ({ row: rows[k].row, lineIds: idsOf(k), price: rows[k].price, ...extra(k) }))
        .filter(p => p.lineIds.length);
      contracts.push({
        ...(products.length > 1
          ? { mode: 'new', products, ...newContractFields(settingsOf(o, shared)) }
          : { ...products[0], mode: 'new', ...newContractFields(settingsOf(o, shared)) }),
        ...groupPayload(rows, offers, groups.get(o.shared))
      });
      return;
    }
    const c = { row: r.row, lineIds: ids, price: r.price, ...extra(i), mode: o.mode === 'existing' ? 'existing' : 'new' };
    if (c.mode === 'new') {
      Object.assign(c, newContractFields(settingsOf(o, shared)), groupPayload(rows, offers, [i]));
    } else {
      const g = (r.contract_offer.existing || []).find(x => x.key === o.contractKey);
      c.contractId = g ? g.anchor_id : null;
      c.others = { ...defaultOthers(g), ...(o.others || {}) };
    }
    contracts.push(c);
  });
  return contracts;
}

/** `group_products` + `product_group_id` for a new contract of these rows, or nothing. */
function groupPayload(rows, offers, members) {
  const gp = groupProductsFor(rows, offers, members);
  if (!gp.products.length) return {};
  return { product_group_id: gp.groupId, group_products: gp.products.map(x => ({ item_id: x.item_id, price: x.price })) };
}

/** " — and from <group>: A at £x; B at £y" for the summary. */
function groupWords(rows, offers, members) {
  const gp = groupProductsFor(rows, offers, members);
  if (!gp.products.length) return '';
  return `; with ${gp.products.length} more from the product group ${gp.groupName}: ${gp.products.map(x => `${x.item_code}${x.item_name ? ` ${x.item_name}` : ''} at ${money(x.price)}`).join('; ')} (made from the group)`;
}

/** How a contract's lines are said in the summary: linked, or (invoiced sales, 1.2.0) the invoices left as they are. */
function linkWords(co, count, theirs = 'it') {
  if (co && co.no_link) return `made from ${count} invoiced line${count === 1 ? '' : 's'} (the invoices are unchanged)`;
  return `${count} line${count === 1 ? '' : 's'} linked to ${theirs}`;
}

/**
 * "What will be written" for the contracts, in sentences — one per contract,
 * in row order. `idsOf(i)` as contractsPayload.
 */
export function contractSummary({ rows, offers = {}, shared = {}, idsOf }) {
  const lines = [];
  const groups = sharedMembers(rows, offers);
  const said = new Set();
  rows.forEach((r, i) => {
    const ids = idsOf(i);
    if (!ids.length) return;
    const o = offers[i];
    if (!offerOn(r, o)) return;
    const co = r.contract_offer;
    const what = `${co.item_code}${co.item_name ? ` ${co.item_name}` : ''} at ${money(r.price)}`;
    if (o.mode === 'existing') {
      const g = (co.existing || []).find(x => x.key === o.contractKey);
      if (!g) return;
      const others = { ...defaultOthers(g), ...(o.others || {}) };
      const same = g.products.find(p => p.same_product && others[p.id] === 'replace');
      const parts = [same ? `${co.item_code} price ${money(same.price)} → ${money(r.price)} (updated in place)` : `${what} added`];
      g.products.forEach(p => {
        if (p === same) return;
        const a = others[p.id];
        if (a === 'hold' && !p.on_hold) parts.push(`${p.item_code || p.item_name} put on hold`);
        if (a === 'archive' && !p.archived) parts.push(`${p.item_code || p.item_name} archived`);
        if (a === 'replace' && !p.archived) parts.push(`${p.item_code || p.item_name} archived — replaced by ${co.item_code}`);
      });
      lines.push(`${co.account_name}: ${contractGroupText(g)} — ${parts.join('; ')}; ${linkWords(co, ids.length)}.`);
      return;
    }
    const eff = settingsOf(o, shared);
    const members = o.shared ? (groups.get(o.shared) || [i]).filter(k => idsOf(k).length) : [i];
    if (members.length > 1) {
      if (said.has(o.shared)) return;
      said.add(o.shared);
      const products = members.map(k => {
        const c = rows[k].contract_offer;
        return `${c.item_code}${c.item_name ? ` ${c.item_name}` : ''} at ${money(rows[k].price)}`;
      });
      const count = members.reduce((m, k) => m + idsOf(k).length, 0);
      lines.push(`${co.account_name}: one new ${co.category} contract of ${members.length} products — ${products.join('; ')}${groupWords(rows, offers, members)} — ${whenText(co, eff)}, ${co.terms || 'no delivery terms'}, for ${eff.scope === 'group' ? 'its pricing group' : 'this customer'}, ${quantityWords(eff, co)}; ${linkWords(co, count, 'their product')}.`);
      return;
    }
    lines.push(`${co.account_name}: new ${co.category} contract — ${what}${groupWords(rows, offers, [i])}, ${whenText(co, eff)}, ${co.terms || 'no delivery terms'}, for ${eff.scope === 'group' ? 'its pricing group' : 'this customer'}, ${quantityWords(eff, co)}; ${linkWords(co, ids.length)}.`);
  });
  return lines;
}

/**
 * Join row `j` to row `i`'s new contract, or take it out (`j` null = take `i`
 * out). A contract left with one row goes back to being that row's own.
 * Pure: returns { offers, shared }.
 */
export function joinContract({ offers, shared }, i, j) {
  const nextOffers = { ...offers };
  const nextShared = { ...shared };
  const settingsKeys = ['periodId', 'useDates', 'start', 'end', 'scope', 'quantity'];
  const pick = (o) => Object.fromEntries(settingsKeys.filter(k => o && o[k] !== undefined).map(k => [k, o[k]]));
  if (j === null || j === undefined) {
    const o = nextOffers[i];
    if (o && o.shared) nextOffers[i] = { ...o, ...pick(nextShared[o.shared]), shared: null };
  } else {
    const lead = nextOffers[i] || { on: true, mode: 'new', scope: 'parent' };
    let label = lead.shared;
    if (!label) {
      label = nextLabel(nextShared);
      nextShared[label] = { scope: 'parent', ...pick(lead) };
      nextOffers[i] = { ...lead, on: true, mode: 'new', shared: label };
    }
    nextOffers[j] = { scope: 'parent', ...(nextOffers[j] || {}), on: true, mode: 'new', shared: label };
  }
  // A contract of one row is that row's own again.
  const count = {};
  Object.values(nextOffers).forEach(o => { if (o && o.on && o.mode !== 'existing' && o.shared) count[o.shared] = (count[o.shared] || 0) + 1; });
  Object.entries(nextOffers).forEach(([k, o]) => {
    if (o && o.shared && (count[o.shared] || 0) < 2) nextOffers[k] = { ...o, ...pick(nextShared[o.shared]), shared: null };
  });
  Object.keys(nextShared).forEach(l => { if ((count[l] || 0) < 2) delete nextShared[l]; });
  return { offers: nextOffers, shared: nextShared };
}

/**
 * A row's starting state when it should open on New contract (a rep asked
 * for one; or Pricing & Costing's window, where every row starts on): on, its
 * default period chosen.
 */
export function openedOffer(r) {
  const co = r && r.contract_offer;
  if (!co || !co.available) return null;
  return { on: true, mode: 'new', scope: 'parent', ...(co.default_period_id ? { periodId: String(co.default_period_id) } : {}) };
}

/**
 * "Add the rest of <group>'s plain products to this contract" (1.1.0): the
 * switch, each product with its price (the row's unless typed) and supplier,
 * and the group's products left out with why.
 */
function GroupRest({ id, row, offer, state, onChange, memberItems }) {
  const g = offer.product_group;
  const ga = state.groupAdd || {};
  const on = !!ga.on && g.ready;
  const prices = ga.prices || {};
  const list = (g.add || []).filter(m => !memberItems.has(m.item_id));
  const set = (patch) => onChange({ groupAdd: { ...ga, ...patch } });
  return (
    <div className="rr-contract-field rr-group-rest">
      <div className="rr-group-rest-head" title={g.ready ? '' : g.reason}>
        <div className="checkbox-container">
          <div className="check light">
            <input id={`${id}-grp`} type="checkbox" checked={on} disabled={!g.ready || !list.length}
              onChange={(e) => set({ on: e.target.checked })} />
            <label htmlFor={`${id}-grp`}></label>
          </div>
        </div>
        <label htmlFor={`${id}-grp`} className={g.ready && list.length ? '' : 'pc-muted'}>
          Add the rest of {g.name}'s plain products to this contract{g.pack_words ? ` (${g.pack_words})` : ''}
          {!g.ready ? ` — ${g.reason}` : !list.length ? ' — every one is in this contract already' : ` — ${list.length} more`}
        </label>
      </div>
      {on && list.length > 0 && (
        <table className="rr-line-table rr-group-rest-list">
          <tbody>
            {list.map(m => (
              <tr key={m.item_id}>
                <td>{memberText({ item_code: m.item_code, item_name: m.item_name })}</td>
                <td className="pc-muted">{m.supplier || 'supplier not known'}</td>
                <td className="num">
                  <Input value={prices[m.item_id] ?? ''} placeholder={String(row.price ?? '')} inputMode="decimal"
                    aria-label={`Price for ${m.item_code}`}
                    onChange={(e) => set({ prices: { ...prices, [m.item_id]: e.target.value } })} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {on && <span className="pc-muted">Each at {money(row.price)} unless you type its own. Only this window's lines are linked now; orders for these products count against the contract when priced from it, or after Refresh auto-links.</span>}
      {(g.left_out || []).length > 0 && (
        <span className="pc-muted rr-group-rest-out">
          Left out: {g.left_out.map(m => `${m.item_code} — ${m.why}`).join('; ')}.
        </span>
      )}
    </div>
  );
}

/**
 * The panel: a row's contract — a new one (of this row's product alone, or
 * with other rows' products: one contract, a row per product), or added to
 * one of the customer's, with what happens to the products it already holds.
 *
 * @param {object}   props.row       the row (price, row, contract_offer)
 * @param {number}   props.index     its index in `rows`
 * @param {object[]} props.rows      every row of the window
 * @param {object}   props.offer     row.contract_offer
 * @param {object}   props.state     offers[index]
 * @param {string}   [props.heading] the switch's words ("Also make this a contract price")
 * @param {string}   [props.request] a rep's request for a contract on this row, in words
 * @param {string}   [props.idPrefix] the element ids' prefix
 */
function ContractFromPricesPanel({
  row, index, rows, offer, state = {}, offers, shared, members, problem, onChange, onShared, onJoin, onLeave,
  heading = 'Also make this a contract price', request = null, idPrefix = 'rr-contract'
}) {
  const on = !!state.on && offer.available;
  const id = `${idPrefix}-${row.row}`;
  const groups = offer.existing || [];
  const group = groups.find(g => g.key === state.contractKey) || null;
  const others = { ...defaultOthers(group), ...(state.others || {}) };
  const eff = settingsOf(state, shared);
  const many = members.length > 1;
  const span = (() => {
    const ds = members.map(k => rows[k].contract_offer?.dates).filter(Boolean);
    return ds.length ? { from: ds.map(d => d.from).sort()[0], to: ds.map(d => d.to).sort().slice(-1)[0] } : offer.dates;
  })();
  const periodOptions = (offer.periods || []).map(p => ({
    value: String(p.id),
    label: `${p.name} (${ukDate(p.start_date)} – ${ukDate(p.end_date)})${offer.no_cover || !span || (p.start_date <= span.from && p.end_date >= span.to) ? '' : ' — does not cover every delivery'}`
  }));
  // Rows whose product can join this new contract: priced, offered, the same
  // customer and category, a product not already in it.
  const memberItems = new Set(members.map(k => rows[k].contract_offer?.item_id));
  const candidates = rows.map((r, k) => ({ r, k })).filter(({ r, k }) => !members.includes(k) && r.price
    && r.contract_offer && r.contract_offer.available
    && r.contract_offer.account_id === offer.account_id && r.contract_offer.category_id === offer.category_id
    && !memberItems.has(r.contract_offer.item_id));
  const candidateOptions = candidates.map(({ r, k }) => {
    const o = offers[k];
    const elsewhere = o && o.on && o.mode !== 'existing' && o.shared && o.shared !== state.shared ? ` (moves it from new contract ${o.shared})` : '';
    return { value: String(k), label: `Row ${r.row} — ${r.contract_offer.item_code}${r.contract_offer.item_name ? ` ${r.contract_offer.item_name}` : ''} at ${money(r.price)}${elsewhere}` };
  });
  const toExisting = () => {
    if (state.shared) onLeave(index);
    onChange({ mode: 'existing' });
  };
  return (
    <div className={`rr-contract ${on ? 'on' : ''} ${on && problem ? 'rr-attn-box' : ''}`}>
      <div className="rr-contract-head">
        <div className="checkbox-container" title={offer.available ? '' : offer.reason}>
          <div className="check light">
            <input id={id} type="checkbox" checked={on} disabled={!offer.available}
              onChange={(e) => {
                if (!e.target.checked && state.shared) onLeave(index);
                onChange({
                  on: e.target.checked,
                  ...(e.target.checked && !state.periodId && offer.default_period_id ? { periodId: String(offer.default_period_id) } : {})
                });
              }} />
            <label htmlFor={id}></label>
          </div>
        </div>
        <label htmlFor={id} className={offer.available ? '' : 'pc-muted'}>
          {heading}{offer.available ? ` — ${offer.item_code} for ${offer.account_name} at ${money(row.price)}` : ` — ${offer.reason}`}
        </label>
      </div>
      {request && (
        <div className="rr-contract-request" role="note">
          <span className="material-symbols-rounded">handshake</span>
          <span><strong>Rep asks for a contract</strong> — {request}</span>
        </div>
      )}
      {request && !on && offer.available && (
        <div className="pc-muted rr-contract-request-off">Left off: the rep's request stays on the lines ("Contract asked for" in Pricing &amp; Costing) until a contract is made from them or someone chooses Not now.</div>
      )}
      {on && (
        <div className="rr-contract-body">
          <div className="rr-answers" role="radiogroup" aria-label="New or existing contract">
            <button type="button" role="radio" aria-checked={state.mode !== 'existing'} className={`pc-chip ${state.mode !== 'existing' ? 'on' : ''}`}
              onClick={() => onChange({ mode: 'new' })}>New contract</button>
            <button type="button" role="radio" aria-checked={state.mode === 'existing'} className={`pc-chip ${state.mode === 'existing' ? 'on' : ''}`}
              disabled={!groups.length} title={groups.length ? '' : `${offer.account_name} has no ${offer.category} contract covering these deliveries or for this product`}
              onClick={toExisting}>Add to an existing contract</button>
          </div>
          {state.mode !== 'existing' ? (
            <div className="rr-contract-grid">
              <label>Products</label>
              <div className="rr-contract-field">
                <div className="rr-products" aria-label="Products in this contract">
                  {members.map(k => {
                    const c = rows[k].contract_offer;
                    return (
                      <span key={k} className={`rr-product ${k === index ? 'this' : ''}`}>
                        <span>{c.item_code}{c.item_name ? ` ${c.item_name}` : ''} at {money(rows[k].price)}</span>
                        <span className="pc-muted">{k === index ? 'this row' : `row ${rows[k].row}`}</span>
                        {many && (
                          <button type="button" className="btn-icon rr-product-out" onClick={() => onLeave(k)}
                            aria-label={`Take ${c.item_code} out of this contract`} title={k === index ? 'Take this row out — its own contract' : `Take row ${rows[k].row} out — its own contract`}>
                            <span className="material-symbols-rounded">close</span>
                          </button>
                        )}
                      </span>
                    );
                  })}
                </div>
                {candidateOptions.length > 0 ? (
                  <div className="rr-add-product">
                    <Dropdown escapeOverflow compact value="" placeholder="Add another row's product to this contract…"
                      options={candidateOptions} onChange={(v) => onJoin(Number(v))} />
                  </div>
                ) : (
                  <span className="pc-muted">{many
                    ? `No other row of ${offer.account_name}'s ${offer.category} products to add.`
                    : `One contract can hold several products: other rows of ${offer.account_name}'s ${offer.category} products with a price can be added here — none here.`}</span>
                )}
                {many && <p className="pc-muted rr-shared-note">One contract of {members.length} products — one row each, the same dates, terms and scope{state.shared ? ` (new contract ${state.shared})` : ''}.</p>}
              </div>
              {offer.product_group && (
                <>
                  <label>Product group</label>
                  <GroupRest id={id} row={row} offer={offer} state={state} onChange={onChange} memberItems={memberItems} />
                </>
              )}
              <label>Period</label>
              <div className="rr-contract-field">
                <Dropdown escapeOverflow compact options={[...periodOptions, { value: '__dates', label: 'Choose From / To dates…' }]}
                  value={eff.useDates ? '__dates' : (eff.periodId || '')} placeholder={`Choose a ${offer.category} period…`}
                  onChange={(v) => (v === '__dates' ? onShared({ useDates: true, start: eff.start || span.from, end: eff.end || span.to }) : onShared({ useDates: false, periodId: v }))} />
              </div>
              {eff.useDates && (
                <>
                  <label>From / To</label>
                  <div className="rr-contract-field rr-dates">
                    <DateInput value={eff.start || ''} onChange={(e) => onShared({ start: e.target.value })} />
                    <DateInput value={eff.end || ''} onChange={(e) => onShared({ end: e.target.value })} />
                  </div>
                </>
              )}
              <label>Delivery terms</label>
              <div className="rr-contract-field">{offer.terms || <span className="pc-muted">none on the order</span>} <span className="pc-muted">{offer.no_link && !offer.terms ? '(the invoices were not raised from an order)' : '(from the order)'}</span></div>
              <label>For</label>
              <div className="rr-answers">
                <button type="button" className={`pc-chip ${eff.scope !== 'group' ? 'on' : ''}`} onClick={() => onShared({ scope: 'parent' })}>{offer.account_name} only</button>
                <button type="button" className={`pc-chip ${eff.scope === 'group' ? 'on' : ''}`} onClick={() => onShared({ scope: 'group' })}>Its pricing group</button>
              </div>
              <label htmlFor={`${id}-qty`}>Quantity</label>
              <div className="rr-contract-field rr-contract-qty">
                <Input id={`${id}-qty`} value={eff.quantity ?? ''} placeholder="Blank" inputMode="decimal"
                  aria-label={`Contract quantity${offer.unit ? ` (${offer.unit === 'T' ? 'tonnes' : offer.unit})` : ''}`}
                  onChange={(e) => onShared({ quantity: e.target.value })} />
                <span className="pc-muted">
                  {offer.unit ? (offer.unit === 'T' ? 'T' : `× ${offer.unit}`) : ''} Left blank unless you type one — it is not taken from the orders
                  {offer.lines_quantity ? ` (${many ? 'this row\'s orders come' : 'they come'} to ${linesQtyText(offer.lines_quantity, offer.unit)})` : ''}.
                  {' '}Set it now, or later on the contract{many ? ' — one quantity for the whole contract' : ''}.
                </span>
              </div>
            </div>
          ) : (
            <div className="rr-contract-grid">
              <label>Contract</label>
              <div className="rr-contract-field">
                <Dropdown escapeOverflow compact placeholder="Choose the contract…" value={state.contractKey || ''}
                  options={groups.map(g => ({ value: g.key, label: `${contractGroupText(g)}${g.covers || offer.no_cover ? '' : ' — does not cover every delivery'}` }))}
                  onChange={(v) => onChange({ contractKey: v, others: {} })} />
              </div>
              {group && (
                <>
                  <label>Its products</label>
                  <table className="rr-line-table rr-contract-products">
                    <tbody>
                      {group.products.map(p => (
                        <tr key={p.id}>
                          <td>{p.item_code || '—'} {p.item_name}{p.same_product ? ' (this product)' : ''}</td>
                          <td className="num">{money(p.price)}</td>
                          <td>{[p.on_hold ? 'on hold' : '', p.archived ? 'archived' : ''].filter(Boolean).join(', ')}</td>
                          <td>
                            <Dropdown escapeOverflow compact value={others[p.id]}
                              options={Object.entries(CONTRACT_OTHER_ACTIONS).map(([value, label]) => ({
                                value,
                                label: value === 'replace' ? (p.same_product ? `Replace its price with ${money(row.price)}` : `Replace with ${offer.item_code} at ${money(row.price)}`) : label
                              }))}
                              onChange={(v) => onChange({ others: { ...(state.others || {}), [p.id]: v } })} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          )}
          {problem && <div className="rr-attn-why">To do: {problem}</div>}
        </div>
      )}
    </div>
  );
}

export default ContractFromPricesPanel;
